import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './AuditLog.css';

function AuditLog() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Pagination
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(50);

  // Filters
  const [filterUserId, setFilterUserId] = useState('');
  const [filterAction, setFilterAction] = useState('');
  const [filterEntityType, setFilterEntityType] = useState('');
  const [availableActions, setAvailableActions] = useState([]);
  const [availableEntityTypes, setAvailableEntityTypes] = useState([]);

  useEffect(() => {
    fetchFilters();
  }, []);

  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filterUserId, filterAction, filterEntityType]);

  const fetchFilters = async () => {
    try {
      const response = await axios.get('/api/admin/audit-logs/filters');
      setAvailableActions(response.data.actions || []);
      setAvailableEntityTypes(response.data.entityTypes || []);
    } catch (err) {
      console.error('Failed to fetch filters:', err);
    }
  };

  const fetchLogs = async () => {
    try {
      setLoading(true);
      setError('');

      const params = {
        limit,
        offset: (page - 1) * limit
      };

      if (filterUserId) params.userId = filterUserId;
      if (filterAction) params.action = filterAction;
      if (filterEntityType) params.entityType = filterEntityType;

      const response = await axios.get('/api/admin/audit-logs', { params });
      setLogs(response.data.logs || []);
      setTotal(response.data.total || 0);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch audit logs');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = () => {
    setPage(1);
    fetchLogs();
  };

  const handleClearFilters = () => {
    setFilterUserId('');
    setFilterAction('');
    setFilterEntityType('');
    setPage(1);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const formatActionName = (action) => {
    return action.split('_').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ).join(' ');
  };

  const getActionBadgeClass = (action) => {
    if (action.includes('delete')) return 'badge-danger';
    if (action.includes('create') || action.includes('register')) return 'badge-success';
    if (action.includes('update') || action.includes('enable')) return 'badge-primary';
    if (action.includes('disable')) return 'badge-warning';
    if (action.includes('login')) return 'badge-info';
    return 'badge-secondary';
  };

  const totalPages = Math.ceil(total / limit);

  if (loading && logs.length === 0) {
    return (
      <div className="audit-log-page">
        <div className="page-header">
          <h1>Audit Log</h1>
        </div>
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading audit logs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="audit-log-page">
      <div className="page-header">
        <h1>Audit Log</h1>
        <p className="page-subtitle">Track all system activities and user actions</p>
      </div>

      {error && (
        <div className="alert alert-danger">
          {error}
        </div>
      )}

      {/* Filters */}
      <div className="filters-section">
        <div className="filters-grid">
          <div className="filter-group">
            <label>Action</label>
            <select
              value={filterAction}
              onChange={(e) => setFilterAction(e.target.value)}
              className="form-control"
            >
              <option value="">All Actions</option>
              {availableActions.map(action => (
                <option key={action} value={action}>
                  {formatActionName(action)}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Entity Type</label>
            <select
              value={filterEntityType}
              onChange={(e) => setFilterEntityType(e.target.value)}
              className="form-control"
            >
              <option value="">All Entity Types</option>
              {availableEntityTypes.map(type => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>User ID</label>
            <input
              type="text"
              value={filterUserId}
              onChange={(e) => setFilterUserId(e.target.value)}
              placeholder="Enter user ID..."
              className="form-control"
            />
          </div>

          <div className="filter-actions">
            <button
              className="btn btn-primary"
              onClick={handleFilterChange}
            >
              Apply Filters
            </button>
            <button
              className="btn btn-secondary"
              onClick={handleClearFilters}
            >
              Clear
            </button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="audit-stats">
        <div className="stat-item">
          <span className="stat-label">Total Entries:</span>
          <span className="stat-value">{total.toLocaleString()}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Showing:</span>
          <span className="stat-value">
            {logs.length === 0 ? 0 : (page - 1) * limit + 1} - {Math.min(page * limit, total)}
          </span>
        </div>
      </div>

      {/* Audit Log Table */}
      <div className="table-container">
        {logs.length === 0 ? (
          <div className="no-data">
            <p>No audit logs found</p>
            {(filterAction || filterEntityType || filterUserId) && (
              <button className="btn btn-secondary" onClick={handleClearFilters}>
                Clear Filters
              </button>
            )}
          </div>
        ) : (
          <table className="audit-table">
            <thead>
              <tr>
                <th>Timestamp</th>
                <th>User</th>
                <th>Action</th>
                <th>Entity Type</th>
                <th>Entity ID</th>
                <th>IP Address</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => (
                <tr key={log.id}>
                  <td className="timestamp-col">
                    {formatDate(log.created_at)}
                  </td>
                  <td className="user-col">
                    <div className="user-info">
                      <div className="user-name">
                        {log.first_name} {log.last_name}
                      </div>
                      <div className="user-email">{log.email}</div>
                    </div>
                  </td>
                  <td>
                    <span className={`badge ${getActionBadgeClass(log.action)}`}>
                      {formatActionName(log.action)}
                    </span>
                  </td>
                  <td>
                    {log.entity_type ? (
                      <code className="entity-type">{log.entity_type}</code>
                    ) : (
                      <span className="text-muted">-</span>
                    )}
                  </td>
                  <td className="entity-id-col">
                    {log.entity_id ? (
                      <code className="entity-id" title={log.entity_id}>
                        {log.entity_id.substring(0, 8)}...
                      </code>
                    ) : (
                      <span className="text-muted">-</span>
                    )}
                  </td>
                  <td>
                    {log.ip_address || <span className="text-muted">-</span>}
                  </td>
                  <td className="details-col">
                    {log.details ? (
                      <details>
                        <summary>View</summary>
                        <pre>{JSON.stringify(log.details, null, 2)}</pre>
                      </details>
                    ) : (
                      <span className="text-muted">-</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button
            className="btn btn-secondary"
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            Previous
          </button>
          <span className="page-info">
            Page {page} of {totalPages}
          </span>
          <button
            className="btn btn-secondary"
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

export default AuditLog;
